import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import styled, { keyframes } from "styled-components";
import { /*convertUnicode, createSafeHTML,*/ fetchApi } from "../../../utils/methods";
import { GenerateChatGPTContent } from "../../../redux/constants/apiConstants";
import PropTypes from "prop-types";
import parse from 'react-html-parser';

const themes = [
  {name:"Date Night ❤️",peoples:'2'},
  {name:"Family Night 🏠",peoples:'6'},
  // "Family Night 🏠",
//   "Quick Dinner ⏱️",
//   "Comfort Food 🍲",
//   "Kids' Favorites ⭐",
//   "Healthy Eating 🥗",
//   "Vegan Delight 🌱",
//   "High Protein 💪",
];

// Styled Components

const Container = styled.div`
  max-width: 480px;
  width:100%;
  margin: 40px auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  height: 500px;
  ${'' /* overflow: hidden; */}
  ${'' /* font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; */}
`;

const Header = styled.div`
  padding: 14px;
  background: #F37A65;
  color: white;
  font-family: Lato;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  user-select: none;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const ChatArea = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageBubble = styled.div`
  max-width: 75%;
  padding: 14px 18px;
  border-radius: 18px;
  white-space: pre-wrap;
  word-break: break-word;
  ${'' /* box-shadow: 0 4px 6px rgba(0,0,0,0.08); */}
  background: ${({ isUser }) => (isUser ? "#D3DEF2" : "#EFEFEF")};
  color: ${({ isUser }) => (isUser ? "#184A61" : "#184A61")};
  align-self: ${({ isUser }) => (isUser ? "flex-end" : "flex-start")};
  position: relative;

  ${'' /* color: #184A61; */}
  ${'' /* text-align: center; */}
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const ButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  padding: 12px 20px;
  background: #fff;
  border-top: 1px solid #EFEFEF;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const ThemeButton = styled.button`
  background: ${({ selected }) => (selected ? "#F37A65" : "#fff")};
  color: ${({ selected }) => (selected ? "#fff" : "#184A61")};
  border: 1px solid #EFEFEF;
  padding: 10px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  ${'' /* box-shadow: ${({ selected }) =>
    selected ? "0 4px 12px rgba(37, 117, 252, 0.5)" : "none"};
  transition: background 0.3s, box-shadow 0.3s;

  &:hover {
    background: #2575fc;
    color: #fff;
    box-shadow: 0 6px 14px rgba(37, 117, 252, 0.7);
  } */}
`;

const LoadingBubble = styled(MessageBubble)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e4e6eb;
  color: #555;
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.6;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  background-color: #666;
  border-radius: 50%;
  display: inline-block;
  animation: ${dotPulse} 1.4s infinite ease-in-out;
  animation-delay: ${({ delay }) => delay};
`;

function LoadingDots() {
  return (
    <>
      <Dot delay="0s" />
      <Dot delay="0.2s" />
      <Dot delay="0.4s" />
    </>
  );
}

export default function RecipeGeneratorChatbot({recipeDetails, ingredients}) {
  const [messages, setMessages] = useState([
    { id: 0, text: "Hi! Pick a recipe theme to get started.", isUser: false },
  ]);
  const [loading, setLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    // Scroll to bottom on new messages
    // chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleThemeSelect = async (theme) => {
    if (loading) return; // prevent multiple calls

    setSelectedTheme(theme);
    setMessages((prev) => [
      ...prev,
      { id: prev.length, text: theme.name, isUser: true },
    ]);
    setLoading(true);

    const payload = {
      model: "gpt-4.1-nano",
      messages: [
        {
          role: "user",
          //   content: `Generate a wholesome recipe for "${theme}". Include ingredients, cooking steps, and a shopping list with ecommerce links. Give direct content.`,
          content: `You're a smart assistant creating shopping lists based on themed recipes.

          Theme: ${theme.name}
          Ingredients JSON:
          ${JSON.stringify(ingredients)}
          Note: These ingredients are for ${recipeDetails?.servings} servings give me it for ${theme?.peoples} peoples.
          
          Your tasks:
          1. Adjust the ingredient list to match the given theme.
          2. Group ingredients into logical categories (e.g., Spices, Baking Essentials, Fruits, Dairy).
          3. Remove duplicates and standardize measurement units (use cups, tsp, tbsp, grams, or ml).
          4. Format the final output using only HTML **fragments** — do NOT include <!DOCTYPE>, <html>, <head>, or <body> tags and anchor tag should open in new tab add _blank on anchor tag.
          5. The shopping list must be markdown-friendly and follow this format:
          
          <h3>🛒 Shopping List:</h3>
          <ul>
            <li><strong>Whole wheat flour</strong> – 2 cups [<a href="https://www.amazon.com/s?k=whole+wheat+flour" target="_blank">🔗 Amazon</a>]</li>
            <li><strong>Baking powder</strong> – 2 tsp [<a href="https://www.walmart.com/search?q=baking+powder" target="_blank">🔗 Walmart</a>]</li>
          </ul>

          Important: All <a> tags must include target="_blank" so they open in a new tab.
          
          Keep the format clean and easy to scan. Do **not** include cooking instructions or any additional text. Only show the final shopping list.`
          
        },
      ],
    };

    const headers = {
      Authorization: `Bearer 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`,
    };

    try {
      const res = await fetchApi(
        GenerateChatGPTContent,
        "POST",
        payload,
        headers,
        null,
        "https://api.openai.com"
      );

      if (res.choices?.length) {
        let content = res.choices[0].message?.content || "";
        // content = content.replace(/\n\n/g, "\n").replace(/[^\w\s.?!,:\-()]/gi, " ");
        setMessages((prev) => [
          ...prev,
          { id: prev.length, text: content, isUser: false },
        ]);
      }
    } catch (error) {
      toast.error("Failed to generate recipe");
      setMessages((prev) => [
        ...prev,
        { id: prev.length, text: "Oops! Something went wrong.", isUser: false },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{display:"flex"}}>
      <Container>
        <Header>Recipe Chatbot 🍽️</Header>
        <ChatArea ref={chatEndRef}>
          {messages.map(({ id, text, isUser }) =>
            isUser ? (
              <MessageBubble key={id} isUser >
                {/* {createSafeHTML(text ? convertUnicode(text).split('\\n').join('<br />')  : '')} */}
                {parse(text)}
              </MessageBubble>
            ) : (
              <MessageBubble key={id} isUser={false}>
                {/* {createSafeHTML(text ? convertUnicode(text).split('\\n').join('<br />')  : '')} */}
                {parse(text)}
              </MessageBubble>
            )
          )}
          {loading && (
            <LoadingBubble>
              Generating recipe
              <LoadingDots />
            </LoadingBubble>
          )}
        </ChatArea>
   
        <ButtonsRow>
          {themes.map((theme) => (
            <ThemeButton
              key={theme.name}
              onClick={() => handleThemeSelect(theme)}
              selected={theme.name === selectedTheme.name}
              disabled={loading}
            >
              {theme.name}
            </ThemeButton>
          ))}
        </ButtonsRow>
      </Container>
    </div>
  );
}

RecipeGeneratorChatbot.propTypes = {
  recipeDetails: PropTypes.object,
  ingredients:PropTypes.array
}
